import React, { useEffect, useState } from 'react';
import TargetSettingsForm from '../TargetSettingsForm';
import { TargetTypeInfo } from '../../models/target-type-info';
import { NodeConfigData } from '../../../../connector/connector-form/interfaces/nodeFormData';
import {
  TARGET_TYPES,
  TargetType,
  TargetTypesEnum
} from '../../models/target-type';
import { SettingsFormSubmissionMode } from '../../../../connector/connector-form/interfaces/submissionMode';
import {
  TRACKER_TARGET_CONFIG_SUBMIT_CLICK,
  TRACKER_TARGET_CONFIG_SUBMIT_FAILED,
  TRACKER_TARGET_CONFIG_SUBMIT_FORM_ERROR,
  TRACKER_TARGET_CONFIG_SUBMIT_FORM_ERROR_CLICK,
  TRACKER_TARGET_CONFIG_SUBMIT_SUCCESS,
  TRACKER_TARGET_CONFIG_TEST_CLICK,
  TRACKER_TARGET_CONFIG_TEST_FAILED,
  TRACKER_TARGET_CONFIG_TEST_FORM_ERROR,
  TRACKER_TARGET_CONFIG_TEST_SUCCESS
} from '../../../../connector/connector-form/tracking/connectorFormTracking';
import { getBackendErrorCodeFromObj } from '../../../../core/utils/request';
import { TargetFactory } from '../../utils';
import { TargetSettingsService } from '../../services/target-settings.service';
import useService from '../../../../injector/hooks/useService';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import ActivateTargetAPI from '../../ActivateTargetAPI';

interface TargetSettingsFormWrapperProps {
  targetType: TargetTypeInfo;
  updateSettings: boolean;
  targetData: NodeConfigData;
  submit: (e: any) => void;
  emitFormDirty?: Function;
}

function TargetSettingsFormWrapper({
  targetType,
  updateSettings = false,
  targetData,
  submit,
  emitFormDirty
}: TargetSettingsFormWrapperProps) {
  const [targetTypeIdentifier, setTargetTypeIdentifier] = useState<TargetTypesEnum>(
    TargetTypesEnum[targetType.type]
  );

  const [targetTypeMetaData, setTargetTypeMetaData] = useState<TargetType>();
  const [currentTargetName, setCurrentTargetName] = useState('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [formError, setFormError] = useState<any>();
  const [formErrorCode, setFormErrorCode] = useState<number>();
  const [formErrorFor, setFormErrorFor] = useState<SettingsFormSubmissionMode>();
  const [formSuccess, setFormSuccess] = useState<any>();

  const analyticsTracker = useAnalyticsTracker();

  const _targetSettingsService = useService(TargetSettingsService);

  useEffect(() => {
    if (targetData) {
      const targetName = targetData?.formData && 'name' in targetData.formData ? targetData.formData['name'] : '';

      setCurrentTargetName(targetName);
    }
  }, [targetData]);

  const testBtnText = () => {
    return connecting ? 'Connecting' : 'Test Connection';
  };

  const submitBtnText = () => {
    if (submitting) {
      return 'Saving';
    }

    const testPrefix = 'Test & ';

    if (updateSettings) {
      return testPrefix + 'Save';
    }

    return testPrefix + 'Continue';
  };

  const canShowSubmissionOverlay = () => {
    return connecting || submitting;
  };

  useEffect(() => {
    setTargetTypeIdentifier(TargetTypesEnum[targetType.type]);
    setTargetTypeMetaData(TARGET_TYPES[targetTypeIdentifier]);
  }, []);

  const formErrorClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_TARGET_CONFIG_SUBMIT_FORM_ERROR_CLICK,
      properties: {
        ..._getSubmissionErrorTrackingData()
      }
    });
  };

  const onTestConnectionClick = () => {
    _clearFormErrorMetaData();
    setFormSuccess(undefined);

    analyticsTracker.eventTrack({
      action: TRACKER_TARGET_CONFIG_TEST_CLICK,
      properties: {
        type: targetTypeIdentifier
      }
    });
  };

  const onTestConnectionFormError = (invalidFields: string[]) => {
    if (invalidFields?.length) {
      analyticsTracker.eventTrack({
        action: TRACKER_TARGET_CONFIG_TEST_FORM_ERROR,
        properties: {
          invalidFields: invalidFields.join(','),
          type: targetTypeIdentifier
        }
      });
    }
  };

  const testConnection = () => {
    _testTarget();
  };

  const onSettingsSubmitClick = () => {
    _clearFormErrorMetaData();
    setFormSuccess(undefined);

    analyticsTracker.eventTrack({
      action: TRACKER_TARGET_CONFIG_SUBMIT_CLICK,
      properties: {
        type: targetTypeIdentifier
      }
    });
  };

  const onSettingsSubmitFormError = (invalidFields: string[]) => {
    if (invalidFields?.length) {
      analyticsTracker.eventTrack({
        action: TRACKER_TARGET_CONFIG_SUBMIT_FORM_ERROR,
        properties: {
          invalidFields: invalidFields.join(','),
          type: targetTypeIdentifier
        }
      });
    }
  };

  const settingsSubmit = () => {
    const request = updateSettings ? _updateTarget : _createTarget;

    request();
  };

  const _clearFormErrorMetaData = () => {

    setFormError(null);
    setFormErrorCode(null);
    setFormErrorFor(undefined);
  };

  const _testTarget = () => {
    setConnecting(true);

    const targetId = targetData.id || null;
    const { name, ...config } = targetData.formData;
    const connectionParams = _targetSettingsService.getConnectionParams(
      targetTypeMetaData,
      config,
      updateSettings
    );

    return ActivateTargetAPI.testTargetConnection(
      targetTypeIdentifier,
      name,
      connectionParams,
      targetId
    )
      .then(() => {
        setConnecting(false);
        setFormSuccess('Connection successful');

        analyticsTracker.eventTrack({
          action: TRACKER_TARGET_CONFIG_TEST_SUCCESS,
          properties: {
            type: targetTypeIdentifier
          }
        });
      })
      .catch(err => {
        setConnecting(false);
        _updateFormErrorMetaData(err, SettingsFormSubmissionMode.TEST);

        analyticsTracker.eventTrack({
          action: TRACKER_TARGET_CONFIG_TEST_FAILED,
          properties: _getSubmissionErrorTrackingData()
        });
      });
  };

  const _createTarget = () => {
    setSubmitting(true);

    const { name, ...config } = targetData.formData;
    const connectionParams = _targetSettingsService.getConnectionParams(
      targetTypeMetaData,
      config,
      updateSettings
    );

    return ActivateTargetAPI.createTarget(targetTypeIdentifier, name, connectionParams)
      .then(res => {
        setSubmitting(false);

        analyticsTracker.eventTrack({
          action: TRACKER_TARGET_CONFIG_SUBMIT_SUCCESS,
          properties: {
            type: targetTypeIdentifier
          }
        });

        _targetConfigSubmitSuccess(res?.data);
      })
      .catch(err => {
        setSubmitting(false);
        _updateFormErrorMetaData(err, SettingsFormSubmissionMode.CONTINUE);

        analyticsTracker.eventTrack({
          action: TRACKER_TARGET_CONFIG_SUBMIT_FAILED,
          properties: _getSubmissionErrorTrackingData()
        });
      });
  };

  const _updateTarget = () => {
    setSubmitting(true);
    const targetId = targetData.id;
    const { name, ...config } = targetData.formData;
    const connectionParams = _targetSettingsService.getConnectionParams(
      targetTypeMetaData,
      config,
      updateSettings
    );

    return ActivateTargetAPI.updateTargetConfig(targetId, name, connectionParams)
      .then(res => {
        setSubmitting(false);
        _targetConfigSubmitSuccess(res?.data);
      })
      .catch(err => {
        setSubmitting(false);
        _updateFormErrorMetaData(err, SettingsFormSubmissionMode.EDIT);
      });
  };

  const _getSubmissionErrorTrackingData = () => {
    return {
      reason: formError,
      errorCode: formErrorCode,
      type: targetTypeIdentifier
    };
  };

  const _updateFormErrorMetaData = (error: any, errorFor: SettingsFormSubmissionMode) => {
    setFormError(error);
    setFormErrorCode(getBackendErrorCodeFromObj(error));
    setFormErrorFor(errorFor);
  };

  const _targetConfigSubmitSuccess = (data?: any) => {
    if (data) {
      const target = TargetFactory(data);
      submit(target);
      return target;
    }
  };

  return (
    <TargetSettingsForm
      targetData={targetData}
      savedTargetName={currentTargetName}
      targetTypeIdentifier={targetTypeIdentifier}
      targetTypeMetaData={targetTypeMetaData}
      updateConnection={updateSettings}
      formError={formError}
      formErrorCode={formErrorCode}
      formErrorFor={formErrorFor}
      formSuccess={formSuccess}
      connecting={connecting}
      submitting={submitting}
      testBtnText={testBtnText()}
      submitBtnText={submitBtnText()}
      canShowSubmissionOverlay={canShowSubmissionOverlay()}
      formErrorClick={formErrorClick}
      testConnectionClick={onTestConnectionClick}
      testConnectionFormErrored={onTestConnectionFormError}
      testConnection={testConnection}
      continueClick={onSettingsSubmitClick}
      continueFormErrored={onSettingsSubmitFormError}
      continue={settingsSubmit}
      markFormDirty={emitFormDirty}
      setFormSuccess={formSuccess}
    />
  );
}

export default TargetSettingsFormWrapper;
